/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  MagnifyingGlassIcon,
  PauseIcon,
  PlayIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { RequestEntry, banIp, clientLabel, getSecuritySummary } from "@/api/requests";
import {
  requestAgo,
  requestStatusTone,
  useLiveRequests,
} from "@/components/homepage/widgets/useLiveRequests";
import { formatTimeWithSettings } from "@/utils/timeSettings";
import { cn } from "@/lib/utils";
import { SecurityPanel } from "./SecurityPanel";
import { IpInfoCard } from "./IpInfoCard";
import { showToast } from "@/components/common/Toast";

type Network = RequestEntry["network"];
type StatusClass = "2" | "3" | "4" | "5";

const NETWORKS: { id: Network; label: string; dot: string; chip: string }[] = [
  { id: "local", label: "Local", dot: "bg-sky-500", chip: "border-sky-500/40 bg-sky-500/10 text-sky-600 dark:text-sky-400" },
  { id: "vpn", label: "VPN", dot: "bg-violet-500", chip: "border-violet-500/40 bg-violet-500/10 text-violet-600 dark:text-violet-400" },
  { id: "external", label: "External", dot: "bg-amber-500", chip: "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400" },
];

const SOURCES: { id: string; label: string }[] = [
  { id: "traefik", label: "Proxy" },
  { id: "ssh", label: "SSH" },
  { id: "fail2ban", label: "Bans" },
  { id: "pveproxy", label: "Proxmox" },
  { id: "pve-firewall", label: "Firewall" },
  { id: "conn", label: "Connections" },
];
const SOURCE_LABEL = Object.fromEntries(SOURCES.map((s) => [s.id, s.label]));

const SHOWN_STEP = 150;

const netMeta = (n: Network) => NETWORKS.find((x) => x.id === n) ?? NETWORKS[2];

function bytes(n?: number): string {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
}

function duration(ms: number): string {
  if (!ms) return "";
  return ms < 1 ? "<1 ms" : ms < 1000 ? `${ms.toFixed(0)} ms` : `${(ms / 1000).toFixed(2)} s`;
}

function clock(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleTimeString(undefined, { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

const Chip: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode; count?: number; className?: string }> = ({
  active,
  onClick,
  children,
  count,
  className,
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={cn(
      "inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-lg border px-2.5 text-[12px] transition-colors",
      active
        ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
        : "border-gray-200 text-gray-600 hover:bg-gray-100 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/5",
      className,
    )}
  >
    {children}
    {count !== undefined && <span className="tabular-nums text-gray-400">{count}</span>}
  </button>
);

const Field: React.FC<{ label: string; value?: React.ReactNode; mono?: boolean }> = ({ label, value, mono }) =>
  value === undefined || value === "" || value === null ? null : (
    <div className="min-w-0">
      <div className="text-[10.5px] uppercase tracking-wide text-gray-400 dark:text-gray-500">{label}</div>
      <div className={cn("break-all text-[12px] text-gray-800 dark:text-gray-200", mono && "font-mono")}>{value}</div>
    </div>
  );

export const LiveRequestsPanel: React.FC = () => {
  const { enabled, rows, isError, clear, baseline } = useLiveRequests(1000);
  const qc = useQueryClient();
  const { data: security } = useQuery({ queryKey: ["request-security"], queryFn: getSecuritySummary, refetchInterval: 30_000, retry: false });
  const ban = useMutation({
    mutationFn: banIp,
    onSuccess: () => {
      showToast("Address banned", "success");
      qc.invalidateQueries({ queryKey: ["request-security"] });
    },
    onError: (e: Error) => showToast(e.message, "error"),
  });

  const [paused, setPaused] = useState(false);
  const [frozen, setFrozen] = useState<RequestEntry[]>([]);
  const [query, setQuery] = useState("");
  const [networks, setNetworks] = useState<Network[]>([]);
  const [sources, setSources] = useState<string[]>([]);
  const [classes, setClasses] = useState<StatusClass[]>([]);
  const [origin, setOrigin] = useState("");
  const [showProbes, setShowProbes] = useState(false);
  const [shown, setShown] = useState(SHOWN_STEP);
  const [open, setOpen] = useState<number | null>(null);

  const source = paused ? frozen : rows;

  // "Last minute" needs a clock; tick it instead of reading Date.now() in render.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 5000);
    return () => window.clearInterval(t);
  }, []);

  const toggle = <T,>(list: T[], v: T, set: (x: T[]) => void) =>
    set(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  const sorted = useMemo(
    () => [...source].sort((a, b) => Date.parse(b.time) - Date.parse(a.time) || b.id - a.id),
    [source],
  );

  const origins = useMemo(() => [...new Set(source.map((r) => r.origin).filter(Boolean))].sort(), [source]);

  const q = query.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      sorted.filter((r) => {
        if (!showProbes && r.probe) return false;
        if (networks.length && !networks.includes(r.network)) return false;
        if (sources.length && !sources.includes(r.source)) return false;
        if (origin && r.origin !== origin) return false;
        if (classes.length && !classes.includes(String(Math.floor(r.status / 100)) as StatusClass)) return false;
        if (q) {
          const hay = `${r.service} ${r.host} ${r.path} ${r.clientIp} ${r.clientHost ?? ""} ${r.method} ${r.status} ${r.user ?? ""} ${r.userAgent ?? ""} ${r.router ?? ""} ${r.message ?? ""} ${r.origin}`.toLowerCase();
          return q.split(/\s+/).every((w) => hay.includes(w));
        }
        return true;
      }),
    [sorted, showProbes, networks, sources, origin, classes, q],
  );

  // Facet counts come from everything the *other* filters let through, so a chip
  // shows what you'd get by turning it on.
  const counts = useMemo(() => {
    const c = { net: {} as Record<string, number>, src: {} as Record<string, number>, cls: {} as Record<string, number> };
    for (const r of sorted) {
      if (!showProbes && r.probe) continue;
      c.net[r.network] = (c.net[r.network] ?? 0) + 1;
      c.src[r.source] = (c.src[r.source] ?? 0) + 1;
      if (r.kind === "http" || r.kind === "auth") {
        const k = String(Math.floor(r.status / 100));
        c.cls[k] = (c.cls[k] ?? 0) + 1;
      }
    }
    return c;
  }, [sorted, showProbes]);

  const summary = useMemo(() => {
    const last = filtered.filter((r) => now - Date.parse(r.time) < 60_000);
    const ips = new Map<string, number>();
    const svc = new Map<string, number>();
    let bad = 0;
    for (const r of filtered) {
      ips.set(r.clientIp, (ips.get(r.clientIp) ?? 0) + 1);
      const k = r.service || r.host;
      svc.set(k, (svc.get(k) ?? 0) + 1);
      if (r.status >= 400) bad++;
    }
    const top = (m: Map<string, number>) => [...m.entries()].sort((a, b) => b[1] - a[1])[0];
    return {
      perMin: last.length,
      errPct: filtered.length ? (bad / filtered.length) * 100 : 0,
      ips: ips.size,
      topIp: top(ips),
      topSvc: top(svc),
    };
  }, [filtered, now]);

  const filtersOn = networks.length + sources.length + classes.length > 0 || !!origin || !!q;
  const reset = () => {
    setNetworks([]);
    setSources([]);
    setClasses([]);
    setOrigin("");
    setQuery("");
  };

  const togglePause = () => {
    if (!paused) setFrozen(rows);
    setPaused((p) => !p);
  };

  if (!enabled) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center dark:border-white/15">
        <p className="text-sm text-gray-700 dark:text-gray-200">The request feed isn't configured.</p>
        <p className="mt-1 text-[12px] text-gray-500 dark:text-gray-400">
          Set <code>[request_log]</code> in config to read Traefik, SSH, Proxmox and other logs.
        </p>
      </div>
    );
  }

  const visible = filtered.slice(0, shown);

  return (
    <div className="@container space-y-4">
      <SecurityPanel onPickIp={setQuery} />
      {/* summary */}
      <div className="grid grid-cols-2 gap-3 @4xl:grid-cols-5">
        {[
          { label: "Last minute", value: `${summary.perMin}`, sub: "events" },
          { label: "Errors", value: `${summary.errPct.toFixed(0)}%`, sub: "4xx / 5xx / failed", warn: summary.errPct >= 20 },
          { label: "Unique clients", value: `${summary.ips}`, sub: `${filtered.length} events shown` },
          { label: "Busiest client", value: summary.topIp?.[0] ?? "—", sub: summary.topIp ? `${summary.topIp[1]} events` : "", mono: true },
          { label: "Busiest service", value: summary.topSvc?.[0] ?? "—", sub: summary.topSvc ? `${summary.topSvc[1]} events` : "" },
        ].map((k) => (
          <div key={k.label} className="min-w-0 rounded-xl border border-gray-200 bg-white px-4 py-3 dark:border-white/10 dark:bg-white/[0.03]">
            <div className="text-[12px] text-gray-500 dark:text-gray-400">{k.label}</div>
            <div
              className={cn(
                "mt-1 truncate text-[20px] font-semibold leading-tight tabular-nums text-gray-900 dark:text-white",
                k.mono && "font-mono text-[16px]",
                k.warn && "text-red-500 dark:text-red-400",
              )}
              title={k.value}
            >
              {k.value}
            </div>
            <div className="truncate text-[11.5px] text-gray-500 dark:text-gray-400">{k.sub}</div>
          </div>
        ))}
      </div>

      {/* filters */}
      <div className="space-y-2 rounded-xl border border-gray-200 bg-white p-3 dark:border-white/10 dark:bg-white/[0.03]">
        <div className="flex flex-wrap items-center gap-2">
          <label className="relative min-w-[200px] flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search IP, service, path, user, user agent…"
              className="h-8 w-full rounded-lg border border-gray-200 bg-transparent pl-8 pr-8 text-[12.5px] text-gray-900 outline-none focus:border-emerald-500 dark:border-white/10 dark:text-gray-100"
            />
            {query && (
              <button type="button" aria-label="Clear search" onClick={() => setQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            )}
          </label>
          {origins.length > 1 && (
            <select
              value={origin}
              onChange={(e) => setOrigin(e.target.value)}
              aria-label="Machine"
              className="h-8 rounded-lg border border-gray-200 bg-transparent px-2 text-[12.5px] text-gray-700 dark:border-white/10 dark:bg-[#1a1713] dark:text-gray-200"
            >
              <option value="">All machines</option>
              {origins.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          )}
          <button
            type="button"
            onClick={togglePause}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-gray-200 px-3 text-[12.5px] text-gray-700 hover:bg-gray-100 dark:border-white/10 dark:text-gray-200 dark:hover:bg-white/5"
          >
            {paused ? <PlayIcon className="h-3.5 w-3.5" /> : <PauseIcon className="h-3.5 w-3.5" />}
            {paused ? "Resume" : "Pause"}
          </button>
          <button
            type="button"
            onClick={() => {
              clear();
              setFrozen([]);
              setOpen(null);
            }}
            title="Clear the list (new events keep arriving)"
            className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-gray-200 px-3 text-[12.5px] text-gray-700 hover:bg-gray-100 dark:border-white/10 dark:text-gray-200 dark:hover:bg-white/5"
          >
            <TrashIcon className="h-3.5 w-3.5" />
            Clear
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="mr-0.5 text-[11px] uppercase tracking-wide text-gray-400">From</span>
            {NETWORKS.map((n) => (
              <Chip key={n.id} active={networks.includes(n.id)} onClick={() => toggle(networks, n.id, setNetworks)} count={counts.net[n.id] ?? 0}>
                <span className={cn("h-1.5 w-1.5 rounded-full", n.dot)} />
                {n.label}
              </Chip>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="mr-0.5 text-[11px] uppercase tracking-wide text-gray-400">Source</span>
            {SOURCES.filter((s) => (counts.src[s.id] ?? 0) > 0 || sources.includes(s.id)).map((s) => (
              <Chip key={s.id} active={sources.includes(s.id)} onClick={() => toggle(sources, s.id, setSources)} count={counts.src[s.id] ?? 0}>
                {s.label}
              </Chip>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="mr-0.5 text-[11px] uppercase tracking-wide text-gray-400">Status</span>
            {(["2", "3", "4", "5"] as StatusClass[]).map((c) => (
              <Chip key={c} active={classes.includes(c)} onClick={() => toggle(classes, c, setClasses)} count={counts.cls[c] ?? 0}>
                {c}xx
              </Chip>
            ))}
          </div>
          <Chip active={showProbes} onClick={() => setShowProbes((v) => !v)} className="ml-auto">
            Show health checks
          </Chip>
          {filtersOn && (
            <button type="button" onClick={reset} className="text-[12px] text-emerald-600 hover:underline dark:text-emerald-400">
              Reset filters
            </button>
          )}
        </div>
      </div>

      {/* list */}
      <div className="@container overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/10 dark:bg-white/[0.03]">
        {visible.length === 0 ? (
          <div className="py-12 text-center text-[13px] text-gray-500 dark:text-gray-400">
            {isError ? "unavailable" : filtersOn ? "Nothing matches these filters." : "waiting for traffic…"}
          </div>
        ) : (
          <ul className="divide-y divide-gray-100 font-mono text-[11.5px] dark:divide-white/5">
            {visible.map((r) => {
              const net = netMeta(r.network);
              const isOpen = open === r.id;
              return (
                <li key={r.id} className={r.id > baseline ? "req-in" : undefined}>
                  <button
                    type="button"
                    onClick={() => setOpen(isOpen ? null : r.id)}
                    aria-expanded={isOpen}
                    className={cn(
                      "flex w-full flex-wrap items-center gap-x-2.5 gap-y-0.5 px-3 py-1.5 text-left hover:bg-gray-50 dark:hover:bg-white/[0.04]",
                      isOpen && "bg-gray-50 dark:bg-white/[0.04]",
                    )}
                  >
                    <span className="w-[58px] flex-none tabular-nums text-gray-400 dark:text-gray-500">{clock(r.time)}</span>
                    <span className={cn("flex-none rounded-[3px] border px-1 tabular-nums", requestStatusTone(r.status))}>{r.status || "—"}</span>
                    <span className="w-10 flex-none text-gray-500 dark:text-gray-400">{r.method}</span>
                    <span className="max-w-[26%] flex-none truncate font-sans font-medium text-gray-800 dark:text-gray-200">{r.service || r.host}</span>
                    <span className="min-w-[120px] flex-1 truncate text-gray-500 dark:text-gray-400">{r.path}</span>
                    {/* narrow list: always break here, so every row is the same two lines instead of wrapping wherever the text happens to end */}
                    <span className="h-0 basis-full @[880px]:hidden" aria-hidden="true" />
                    <span className={cn("w-[68px] flex-none rounded-[3px] border px-1 text-center font-sans text-[10.5px]", net.chip)}>{net.label}</span>
                    <span className="w-[170px] flex-none truncate text-gray-600 dark:text-gray-300" title={r.clientHost ? `${r.clientHost} (${r.clientIp})` : r.clientIp}>{clientLabel(r)}</span>
                    <span className="hidden w-[72px] flex-none truncate rounded-[3px] bg-gray-100 px-1 text-center font-sans text-[10.5px] text-gray-500 sm:inline-block dark:bg-white/10 dark:text-gray-400">
                      {SOURCE_LABEL[r.source] ?? r.source}
                    </span>
                    <span className="hidden w-14 flex-none text-right tabular-nums text-gray-400 md:inline dark:text-gray-500">{duration(r.durationMs)}</span>
                    <span className="w-7 flex-none text-right tabular-nums text-gray-300 dark:text-gray-600">{requestAgo(r.time)}</span>
                  </button>

                  {isOpen && (
                    <div className="space-y-3 border-t border-gray-100 bg-gray-50/60 px-4 py-3 font-sans dark:border-white/5 dark:bg-white/[0.02]">
                      {r.network === "external" && <IpInfoCard ip={r.clientIp} />}
                      <div className="grid grid-cols-2 gap-x-6 gap-y-2.5 sm:grid-cols-3 lg:grid-cols-4">
                        <Field label="Time" value={`${new Date(r.time).toLocaleDateString()} ${formatTimeWithSettings(r.time)}`} />
                        <Field label="Source" value={`${SOURCE_LABEL[r.source] ?? r.source} · ${r.origin}`} />
                        <Field label="Client" value={`${r.clientIp} (${net.label})`} mono />
                        <Field label="User" value={r.user} mono />
                        <Field label="Host" value={r.host} mono />
                        <Field label="Service" value={r.service} />
                        <Field label="Router" value={r.router} mono />
                        <Field label="Upstream" value={r.upstream} mono />
                        <Field label="Request" value={r.kind === "http" ? `${r.method} ${r.path}` : r.path} mono />
                        <Field label="Status" value={r.status || undefined} />
                        <Field label="Duration" value={duration(r.durationMs)} />
                        <Field label="Sent / received" value={r.bytesOut || r.bytesIn ? `${bytes(r.bytesOut) || "0 B"} / ${bytes(r.bytesIn) || "0 B"}` : undefined} />
                        <Field label="Protocol" value={[r.scheme, r.protocol, r.tls].filter(Boolean).join(" · ")} />
                        <Field label="Entry point" value={r.entryPoint} />
                        <Field label="Port" value={r.port || undefined} />
                        <Field label="Detail" value={r.message} />
                      </div>
                      {r.userAgent && <Field label="User agent" value={r.userAgent} mono />}
                      <div className="flex flex-wrap gap-2 pt-1">
                        {[
                          ["Only this client", () => setQuery(r.clientIp)],
                          ["Only this service", () => setQuery(r.service || r.host)],
                          ["Only this path", () => setQuery(r.path)],
                        ].map(([label, fn]) => (
                          <button
                            key={label as string}
                            type="button"
                            onClick={fn as () => void}
                            className="h-7 rounded-lg border border-gray-200 px-2.5 text-[12px] text-gray-700 hover:bg-gray-100 dark:border-white/10 dark:text-gray-200 dark:hover:bg-white/5"
                          >
                            {label as string}
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={() => void navigator.clipboard?.writeText(r.clientIp)}
                          className="h-7 rounded-lg border border-gray-200 px-2.5 text-[12px] text-gray-700 hover:bg-gray-100 dark:border-white/10 dark:text-gray-200 dark:hover:bg-white/5"
                        >
                          Copy IP
                        </button>
                        {security?.canBan && r.network === "external" && (
                          <button
                            type="button"
                            disabled={ban.isPending}
                            onClick={() => window.confirm(`Ban ${r.clientIp}${r.clientHost ? ` (${r.clientHost})` : ""}? It will be blocked on this server.`) && ban.mutate(r.clientIp)}
                            className="h-7 rounded-lg border border-red-500/40 px-2.5 text-[12px] text-red-600 hover:bg-red-500/10 dark:text-red-400"
                          >
                            Ban this address
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {filtered.length > visible.length && (
          <button
            type="button"
            onClick={() => setShown((n) => n + SHOWN_STEP)}
            className="w-full border-t border-gray-100 py-2 text-[12.5px] text-emerald-600 hover:bg-gray-50 dark:border-white/5 dark:text-emerald-400 dark:hover:bg-white/[0.04]"
          >
            Show more ({filtered.length - visible.length} left)
          </button>
        )}
      </div>
      <p className="text-[11.5px] text-gray-500 dark:text-gray-400">Health checks (HEAD) are hidden by default.</p>
    </div>
  );
};
